import { CompanyResponse } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import {
    getFallbackErrorKey,
    getImageRejectionCode,
    isApiError
} from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useCompanyPermissions } from '../hooks';
import { addOrUpdateAllCompaniesCache } from '../queries/company-cache';

type UseUploadCompanyImageActionProps = {
    companyId: string;
};

type UseUploadCompanyImageActionData = {
    /** Whether this viewer can change the company's logo right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    uploadImage: (file: File) => Promise<CompanyResponse>;
    loading: boolean;
    error: string;
};

/**
 * Replacing the company's logo: whether this viewer may do it, why not when
 * they may not, and the call itself. The file is rejected by the server rather
 * than the client, so the refusals it can give back are spelled out here.
 */
export function useUploadCompanyImageAction(
    props: UseUploadCompanyImageActionProps
): UseUploadCompanyImageActionData {
    const { companyId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canEditGeneralSettings } = useCompanyPermissions();

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditGeneralSettings) {
            return [
                false,
                t('actions.upload_company_image.reason_not_permitted')
            ];
        }

        return [true, ''];
    }, [canEditGeneralSettings, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/companies/{id}/image` declares gets a branch, plus the
    // rejection codes the endpoint sends but does not declare.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            switch (getImageRejectionCode(error)) {
                case 'UNSUPPORTED_FILE_FORMAT':
                    return t('shared.errors.unsupported_file_format');
                case 'UNSUPPORTED_FILE_SIZE':
                    return t('shared.errors.unsupported_file_size');
                case 'UNSUPPORTED_IMAGE_DIMENSIONS':
                    return t('shared.errors.unsupported_image_dimensions');
            }

            if (isApiError(error, 'CANNOT_PROCESS_FILE')) {
                return t('shared.errors.cannot_process_file');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('shared.errors.company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (file: File) =>
            openApiRef.current.companies
                .uploadCompanyImage(companyId, { file })
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllCompaniesCache(queryClient, data.data.company);
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        uploadImage: (file: File): Promise<CompanyResponse> =>
            mutation.mutateAsync(file),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
